import type { ReactElement } from "react";
import { Verweis } from "@kundeninfo/ui/me-design";
import { istSprungBezug } from "./objektBezug.js";
import type { ObjektBezug } from "./objektBezug.js";
import { OBJEKT_TEXT } from "./objektTexte.js";
import { useObjektPanel } from "./ObjektPanelKontext.js";
import type { ObjektPanelWege } from "./ObjektPanelKontext.js";

export type ObjektVerweisProps = {
  bezug: ObjektBezug;
  /** Nach dem Schließen eines Panels, in dem geschrieben wurde (F9). */
  onGeaendert?: () => void;
  /** Detailposten und das Objekt in seiner eigenen Akte: kein Sprung (115, P9). */
  ohneSprung?: boolean;
  /** Der eigene Weg der Stelle für den Panelknopf, nie der Gastgeber: Objekte mit eigenem Panel ihres Bereichs (Konto,
   *  Planposition, F15; PF5), das Ticket im Ticketdialog (Eltern- und Kind-Ticket, Meilenstein, K8–K10). Der Sprung folgt den
   *  Regeln wie sonst — Konto, Planposition und Meilenstein haben keinen Ort, `ohneSprung` nimmt ihn weg. */
  eigenesPanel?: () => void;
  /** Kein Panelknopf, nur der Sprung: das Objekt in seiner eigenen Zeile ohne Bearbeiten (stornierte Eingangsrechnung). */
  ohnePanel?: boolean;
  /** Kleine Fassung für die erste Spalte einzeiliger Zeilen (115, W-1); `ErsteSpalte` setzt sie selbst. */
  klein?: boolean;
};

export type VerweisTexte = { panel?: string; sprung?: string };

/**
 * Welche Knöpfe der Verweis an dieser Stelle trägt (F1, P3): ein Knopf entsteht, indem sein Text gesetzt wird — Rechte,
 * Stufe und eigenes Panel entscheiden hier als „Text oder nicht“.
 */
export function verweisTexte(w: ObjektPanelWege, p: ObjektVerweisProps): VerweisTexte {
  const b = p.bezug;
  const panel = b.art !== "akte" && p.ohnePanel !== true && (p.eigenesPanel !== undefined || w.kannOeffnen(b)) ? OBJEKT_TEXT.panel(b) : undefined;
  const sprung = p.ohneSprung !== true && istSprungBezug(b) && w.kannSpringen(b)
    ? OBJEKT_TEXT.sprung(b) : undefined;
  return { ...(panel === undefined ? {} : { panel }), ...(sprung === undefined ? {} : { sprung }) };
}

/**
 * Panel- und Sprungknopf hinter dem Namen eines Objekts (`me-verweis`, Entscheide 690/693/107/108): Panel nur, wenn der
 * Gastgeber es öffnen kann; Sprung nur, wenn der Ort erreichbar ist. Aus einem Dialog heraus öffnet das Panel seitlich.
 * Ohne Text kein Element (PF17).
 */
export function ObjektVerweis(p: ObjektVerweisProps): ReactElement | null {
  const wege = useObjektPanel();
  const texte = verweisTexte(wege, p);
  if (texte.panel === undefined && texte.sprung === undefined) return null;
  return (
    <Verweis {...texte} {...(p.klein === true ? { klein: true } : {})}
      onPanel={p.eigenesPanel ?? (() => wege.oeffne(p.bezug, p.onGeaendert === undefined ? {} : { onGeaendert: p.onGeaendert }))}
      onSprung={() => wege.springe(p.bezug)} />
  );
}
